import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteAgentDirectorate, useFilterAgentDirectorates } from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import type { AgentDirectorate } from "../../types";
import { AgentTypeTagWithName } from "../tags/AgentTypeTagWithName";

type Props = {
  onChange: (agentDirectorate: AgentDirectorate) => void;
};

export const AgentDirectorateTable = ({ onChange }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const searchParams = useFilterSearchParams();

  const { data, isLoading } = useFilterAgentDirectorates(searchParams);
  const deleteAgentDirectorate = useDeleteAgentDirectorate();

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const handleDeleteAgentDirectorate = async (id: UUID) => {
    await deleteAgentDirectorate.mutateAsync(id);
  };

  const columns: ColumnsType<AgentDirectorate> = [
    {
      key: "code",
      title: t("agent.directorates.attrs.code"),
      ellipsis: { showTitle: false },
      width: 220,
      render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
    },
    {
      key: "name",
      title: t("agent.directorates.attrs.name"),
      ellipsis: { showTitle: false },
      width: 250,
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "headAgent",
      title: t("agent.directorates.attrs.headAgent"),
      ellipsis: { showTitle: false },
      width: 250,
      render: (_, record) => <AgentTypeTagWithName agent={record.headAgent} />,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onChange(record)} />
          <Divider orientation="vertical" />

          <Popconfirm
            title={t("agent.directorates.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => handleDeleteAgentDirectorate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Table<AgentDirectorate>
      {...tableStandardProps()}
      loading={isLoading}
      columns={columns}
      dataSource={data?.pageData}
      pagination={{
        ...data,
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
      }}
      scroll={{ x: TableSizes.MEDIUM }}
      onChange={handleTableChange}
    />
  );
};
